import { TwitchatDataTypes } from "@/types/TwitchatDataTypes";
import type { StoreActions, StoreGetters } from "@/types/pinia-helpers";
import ApiHelper from "@/utils/ApiHelper";
import Config from "@/utils/Config";
import SetIntervalWorker from "@/utils/SetIntervalWorker";
import { toast } from "@/utils/toast/toast";
import Utils from "@/utils/Utils";
import TwitchUtils from "@/utils/twitch/TwitchUtils";
import * as Sentry from "@sentry/vue";
import { acceptHMRUpdate, defineStore } from "pinia";
import DataStore from "../DataStore";
import type { IStreamlabsActions, IStreamlabsGetters, IStreamlabsState } from "../StoreProxy";
import StoreProxy from "../StoreProxy";

let socket: WebSocket | undefined = undefined;
let pingInterval: string = "";
let reconnectTimeout: number = -1;
let reconnectAttempts: number = 0;
let charityRefreshTimeout: number = -1;
let isAutoInit: boolean = false;
let autoReconnect: boolean = false;
let donationPageIndex: number = 0;
let donationPrevPagesTotal: number = 0;
const tokenRefreshMargin: number = 24 * 60 * 60 * 1000;

export const storeStreamlabs = defineStore("streamlabs", {
	state: (): IStreamlabsState => ({
		accessToken: "",
		refreshToken: "",
		tokenExpirationDate: 0,
		socketToken: "",
		profile: null,
		connected: false,
		authResult: { code: "", csrf: "" },
		charityTeam: null,
		syncingTips: false,
		syncingCampaign: false,
		syncingLeaderboard: false,
	}),

	getters: {
		isLoading: () => {
			return (
				StoreProxy.streamlabs.syncingTips ||
				StoreProxy.streamlabs.syncingCampaign ||
				StoreProxy.streamlabs.syncingLeaderboard
			);
		},
	} satisfies StoreGetters<IStreamlabsGetters, IStreamlabsState>,

	actions: {
		async populateData(): Promise<void> {
			const sessionJSON = DataStore.get(DataStore.STREAMLABS);
			if (sessionJSON) {
				const json = JSON.parse(sessionJSON) as StreamlabsStoreData;
				this.accessToken = json.accessToken;
				this.refreshToken = json.refreshToken || "";
				this.tokenExpirationDate = json.tokenExpirationDate || 0;
				this.charityTeam = json.charityTeam;
				if (this.accessToken) {
					isAutoInit = true;

					// is refresh token is expired or about to expire?
					if (
						this.refreshToken &&
						this.tokenExpirationDate > 0 &&
						Date.now() > this.tokenExpirationDate - tokenRefreshMargin
					) {
						await this.refreshAccessToken();
					}

					const requestSocketToken = () => {
						return ApiHelper.call(
							"streamlabs/socketToken",
							"GET",
							undefined,
							false,
							undefined,
							{ "X-Streamlabs-Token": this.accessToken },
						);
					};

					let result = await requestSocketToken();

					// failed getting token, attempt to refresh and try again
					if (!result.json.success && result.status == 401) {
						if (await this.refreshAccessToken()) {
							result = await requestSocketToken();
						}
					}

					if (result.json.success) {
						this.socketToken = result.json.socketToken || "";
						void this.connect(this.socketToken);
					} else {
						toast(StoreProxy.i18n.t("error.streamlabs_connect_failed"), {
							type: "error",
							autoClose: false,
						});
					}
				}
				if (this.charityTeam) {
					//Get fresh new data
					void this.loadCharityCampaignInfo(this.charityTeam.teamURL);
				}
			}
			let cache = DataStore.get(DataStore.STREAMLABS_CHARITY_CACHE);
			if (cache) {
				const json = JSON.parse(cache) as { page: number; amount: number };
				if (donationPageIndex > 0) {
					donationPageIndex = json.page;
					donationPrevPagesTotal = json.amount;
				}
			}
		},

		async getOAuthURL(): Promise<string> {
			const csrfToken = await ApiHelper.call("auth/CSRFToken", "GET");
			const redirectURI =
				document.location.origin +
				StoreProxy.router.resolve({ name: "streamlabs/auth" }).href;
			const url = new URL("https://streamlabs.com/api/v2.0/authorize");
			url.searchParams.set("client_id", Config.instance.STREAMLABS_CLIENT_ID);
			url.searchParams.set("redirect_uri", redirectURI);
			url.searchParams.set("scope", "socket.token");
			url.searchParams.set("response_type", "code");
			url.searchParams.set("state", csrfToken.json.token);
			return url.href;
		},

		setAuthResult(code: string, csrf: string): void {
			this.authResult.code = code;
			this.authResult.csrf = csrf;
		},

		async getAccessToken(): Promise<boolean> {
			try {
				const csrfResult = await ApiHelper.call("auth/CSRFToken", "POST", {
					token: this.authResult.csrf,
				});
				if (!csrfResult.json.success) return false;
				const result = await ApiHelper.call(
					"streamlabs/auth",
					"POST",
					this.authResult,
					false,
				);
				if (result.json.success) {
					this.accessToken = result.json.accessToken!;
					this.refreshToken = result.json.refreshToken || "";
					this.tokenExpirationDate = result.json.expiresIn
						? Date.now() + result.json.expiresIn * 1000
						: 0;
					this.socketToken = "";
					this.saveData();
					return await this.connect(result.json.socketToken!);
				}
				return false;
			} catch (_error) {
				return false;
			}
		},

		async refreshAccessToken(): Promise<boolean> {
			if (!this.refreshToken) return false;
			try {
				const result = await ApiHelper.call(
					"streamlabs/token/refresh",
					"POST",
					{ refreshToken: this.refreshToken },
					false,
				);
				if (!result.json.success || !result.json.accessToken) return false;
				this.accessToken = result.json.accessToken;
				//Streamlabs may or may not rotate the refresh token, keep the old one if it doesn't
				if (result.json.refreshToken) this.refreshToken = result.json.refreshToken;
				this.tokenExpirationDate = result.json.expiresIn
					? Date.now() + result.json.expiresIn * 1000
					: 0;
				this.saveData();
				return true;
			} catch (_error) {
				return false;
			}
		},

		async connect(token: string, isReconnect: boolean = false): Promise<boolean> {
			// if(!StoreProxy.auth.isPremium) return Promise.resolve(false);

			//Token changed
			if (isReconnect && token != this.socketToken) return Promise.resolve(false);

			this.disconnect(false);

			if (!isReconnect) {
				this.socketToken = token;
				this.saveData();
			}

			// is refresh token is expired or about to expire?
			if (
				this.refreshToken &&
				this.tokenExpirationDate > 0 &&
				Date.now() > this.tokenExpirationDate - tokenRefreshMargin
			) {
				await this.refreshAccessToken();
			}

			try {
				const loadProfile = () => {
					return fetch("https://streamlabs.com/api/v2.0/user", {
						headers: { Authorization: "Bearer " + this.accessToken },
						method: "GET",
					});
				};

				let profileResult = await loadProfile();

				// profile loading fail, refresh token and try again
				if (profileResult.status == 401 && (await this.refreshAccessToken())) {
					profileResult = await loadProfile();
				}

				if (profileResult.status == 200) {
					const json = (await profileResult.json()) as StreamlabsUserData;
					if (json.streamlabs) {
						this.profile = {
							avatar: json.streamlabs.thumbnail,
							name: json.streamlabs.display_name ?? json.streamlabs.username,
						};
					}
				}
			} catch (_error) {}

			autoReconnect = true;

			return new Promise<boolean>((resolve, _reject) => {
				socket = new WebSocket(
					`wss://sockets.streamlabs.com/socket.io/?EIO=3&transport=websocket&token=${this.socketToken}`,
				);

				socket.onopen = async () => {
					reconnectAttempts = 0;
					clearTimeout(reconnectTimeout);
				};

				socket.onmessage = (event: MessageEvent<string>) => {
					//PONG messages
					if (event.data == "3") return;
					//Connection acknwoledgment
					if (event.data == "40") {
						this.connected = true;
						resolve(true);
						return;
					}
					try {
						let codeLength = 0;
						for (; codeLength < event.data.length; codeLength++) {
							if (!/[0-9]/.test(event.data[codeLength]!)) break;
						}
						const code = event.data.substring(0, codeLength);
						const json = JSON.parse(event.data.replace(new RegExp("^" + code, ""), ""));
						//Welcome message
						if (code == "0") {
							const message = json as StreamlabsWelcomeData;
							//Send PING command regularly
							if (pingInterval) SetIntervalWorker.instance.delete(pingInterval);
							pingInterval = SetIntervalWorker.instance.create(() => {
								socket?.send("2");
							}, message.pingInterval || 10000);
						} else //Authentication failed
						if (code == "44") {
							//Show error on top of page
							if (isAutoInit) {
								toast(StoreProxy.i18n.t("error.streamlabs_connect_failed"), {
									type: "error",
									autoClose: false,
								});
							}
							this.disconnect(false);
							resolve(false);
						} else {
							const isPremium = StoreProxy.auth.isPremium;
							if (Array.isArray(json)) {
								const me = StoreProxy.auth.twitch.user;
								json.forEach(async (entry) => {
									if (typeof entry == "string") return;
									const message = entry as
										| StreamlabsDonationData
										| StreamlabsYoutubeSponsorData
										| StreamlabsYoutubeSuperchatData
										| StreamlabsMerchData
										| StreamlabsPatreonPledgeData
										| StreamlabsCharityDonationData;
									switch (message.type) {
										case "streamlabscharitydonation": {
											void ApiHelper.call("log", "POST", {
												cat: "streamlabs",
												log: message,
											});
											if (!this.charityTeam) break;
											//Parse all donations
											message.message.forEach((message) => {
												// console.log(message, this.charityTeam);

												//Ignore if not for currently configure campaign ID
												if (
													message.campaignId !=
														this.charityTeam?.campaignId &&
													!message.isTest
												)
													return;

												const chunks = TwitchUtils.parseMessageToChunks(
													message.message,
													undefined,
													true,
												);
												const to = message.to?.name || me.login;
												const isToSelf =
													to.toLowerCase() == me.login.toLowerCase() ||
													to.toLowerCase() ==
														me.displayNameOriginal.toLowerCase();
												const amount = parseFloat(message.amount);
												this.charityTeam!.amountRaised_cents +=
													amount * 100;
												if (isToSelf) {
													this.charityTeam!.amountRaisedPersonnal_cents +=
														amount * 100;
												}
												const data: TwitchatDataTypes.StreamlabsCharityData =
													{
														id: Utils.getUUID(),
														eventType: "charity",
														platform: "twitch",
														channel_id: me.id,
														type: TwitchatDataTypes.TwitchatMessageType
															.STREAMLABS,
														date: Date.now(),
														amount,
														amountFormatted:
															message.formatted_amount ||
															message.formattedAmount,
														goal:
															this.charityTeam!.amountGoal_cents /
															100,
														goalFormatted:
															this.charityTeam!.amountGoal_cents /
																100 +
															this.charityTeam!.currency,
														totalRaised:
															this.charityTeam!.amountRaised_cents /
															100,
														totalRaisedFormatted:
															this.charityTeam!.amountRaised_cents /
																100 +
															this.charityTeam!.currency,
														campaign: {
															id: this.charityTeam!.cause.id,
															title: this.charityTeam!.cause.title,
															url: this.charityTeam!.teamURL,
														},
														to,
														currency: message.currency ?? "",
														message: message.message,
														message_chunks: chunks,
														message_html:
															TwitchUtils.messageChunksToHTML(chunks),
														userName: message.from,
														isToSelf,
													};
												void StoreProxy.chat.addMessage(data);
											});

											clearTimeout(charityRefreshTimeout);
											charityRefreshTimeout = window.setTimeout(() => {
												//Load fresh new charity data
												void this.loadCharityCampaignInfo();
											}, 5000);
											break;
										}
										case "donation": {
											//Reject if not premium
											if (!isPremium) return;
											message.message.forEach((message) => {
												const chunks = TwitchUtils.parseMessageToChunks(
													message.message,
													undefined,
													true,
												);
												const data: TwitchatDataTypes.StreamlabsDonationData =
													{
														id: Utils.getUUID(),
														eventType: "donation",
														platform: "twitch",
														channel_id: me.id,
														type: TwitchatDataTypes.TwitchatMessageType
															.STREAMLABS,
														date: Date.now(),
														amount: parseFloat(
															message.amount.toString(),
														),
														amountFormatted:
															message.formatted_amount ||
															message.formattedAmount,
														currency: message.currency ?? "",
														message: message.message,
														message_chunks: chunks,
														message_html:
															TwitchUtils.messageChunksToHTML(chunks),
														userName: message.from,
													};
												void StoreProxy.chat.addMessage(data);
											});
											break;
										}
										case "merch": {
											//Reject if not premium
											if (!isPremium) return;
											message.message.forEach((message) => {
												const chunks = TwitchUtils.parseMessageToChunks(
													message.message,
													undefined,
													true,
												);
												const data: TwitchatDataTypes.StreamlabsMerchData =
													{
														id: Utils.getUUID(),
														eventType: "merch",
														platform: "twitch",
														channel_id: me.id,
														type: TwitchatDataTypes.TwitchatMessageType
															.STREAMLABS,
														date: Date.now(),
														product: message.product,
														message: message.message,
														message_chunks: chunks,
														message_html:
															TwitchUtils.messageChunksToHTML(chunks),
														userName: message.from,
													};
												void StoreProxy.chat.addMessage(data);
											});
											break;
										}
										case "pledge": {
											//Reject if not premium
											if (!isPremium) return;
											message.message.forEach((message) => {
												const data: TwitchatDataTypes.StreamlabsPatreonPledgeData =
													{
														id: Utils.getUUID(),
														eventType: "patreon_pledge",
														platform: "twitch",
														channel_id: me.id,
														type: TwitchatDataTypes.TwitchatMessageType
															.STREAMLABS,
														date: Date.now(),
														userName: message.from,
														amount: message.amount,
														amountFormatted:
															message.formatted_amount ||
															message.formattedAmount,
														currency: message.currency ?? "",
													};
												void StoreProxy.chat.addMessage(data);
											});
											break;
										}
									}
								});
							}
						}
					} catch (error) {
						console.log("Failed parsing Streamlabs JSON data");
						console.log(event.data);
						console.error(error);
					}
					isAutoInit = false;
				};

				socket.onclose = (_event) => {
					//Do not reconnect if token changed
					if (token != this.socketToken) return;
					if (!autoReconnect) return;

					this.connected = false;
					if (pingInterval) SetIntervalWorker.instance.delete(pingInterval);
					clearTimeout(reconnectTimeout);
					reconnectAttempts++;
					reconnectTimeout = window.setTimeout(() => {
						socket = undefined;
						void this.connect(token, true);
					}, 500 * reconnectAttempts);
				};

				socket.onerror = (_error) => {
					resolve(false);
					this.connected = false;
					if (!autoReconnect) return;
					reconnectAttempts++;
					if (pingInterval) SetIntervalWorker.instance.delete(pingInterval);
					clearTimeout(reconnectTimeout);
					reconnectTimeout = window.setTimeout(() => {
						socket = undefined;
						void this.connect(token, true);
					}, 500 * reconnectAttempts);
				};
			});
		},

		disconnect(clearStore: boolean = true): void {
			autoReconnect = false;
			if (clearStore) {
				this.socketToken = "";
				this.accessToken = "";
				this.refreshToken = "";
				this.tokenExpirationDate = 0;
				this.charityTeam = null;
				this.saveData();
			}
			if (pingInterval) SetIntervalWorker.instance.delete(pingInterval);
			clearTimeout(reconnectTimeout);
			if (socket && !this.connected) socket.close();
			this.connected = false;
		},

		saveData(): void {
			const data: StreamlabsStoreData = {
				accessToken: this.accessToken,
				refreshToken: this.refreshToken,
				tokenExpirationDate: this.tokenExpirationDate,
				socketToken: this.socketToken,
				charityTeam: this.charityTeam,
			};
			DataStore.set(DataStore.STREAMLABS, data);
		},

		async loadCharityCampaignInfo(url?: string): Promise<boolean> {
			if (!url && this.charityTeam) url = this.charityTeam.teamURL;
			if (!url) return false;
			this.syncingCampaign = true;

			let memberId = "";
			try {
				const parsedUrl = new URL(url);
				memberId = parsedUrl.searchParams.get("member") || "";
			} catch (_error) {}

			try {
				const teamRes = await fetch(
					"https://streamlabscharity.com/api/v1/teams/@" + url.split("@").pop(),
				);
				if (teamRes.status != 200) {
					this.syncingCampaign = false;
					throw new Error("Failed fetching charity team data");
				}
				const prevValues = [];
				if (this.charityTeam) {
					prevValues.push(this.charityTeam.currency);
					prevValues.push(this.charityTeam.amountRaised_cents);
					prevValues.push(this.charityTeam.amountRaisedPersonnal_cents);
				}

				const teamJSON = (await teamRes.json()) as StreamlabsCharityTeamData;
				if (!this.charityTeam) {
					this.charityTeam = {
						id: teamJSON.id,
						teamURL: url,
						title: teamJSON.display_name,
						amountGoal_cents: teamJSON.goal.amount,
						amountRaised_cents: teamJSON.amount_raised,
						amountRaisedPersonnal_cents: 0,
						currency: teamJSON.goal.currency,
						campaignId: teamJSON.campaign.id,
						pageUrl:
							`https://streamlabscharity.com/teams/@${teamJSON.slug}/${teamJSON.campaign.slug}?member=` +
							memberId,
						cause: {
							id: teamJSON.campaign.causable.id,
							title: teamJSON.campaign.causable.display_name,
							description: teamJSON.campaign.causable.description,
							website: teamJSON.campaign.causable.page_settings.website_url,
						},
						avatar: teamJSON.campaign.causable.avatar?.url.replace(/\\\//gi, "/"), //Thanks SL for sending shit url
					};
				} else {
					this.charityTeam.amountGoal_cents = teamJSON.goal.amount;
					this.charityTeam.amountRaised_cents = teamJSON.amount_raised;
				}

				StoreProxy.labels.updateLabelValue(
					"STREAMLABS_CHARITY_GOAL",
					this.charityTeam.amountGoal_cents / 100,
				);
				StoreProxy.labels.updateLabelValue(
					"STREAMLABS_CHARITY_RAISED",
					this.charityTeam.amountRaised_cents / 100,
				);
				StoreProxy.labels.updateLabelValue(
					"STREAMLABS_CHARITY_IMAGE",
					teamJSON.campaign.causable.avatar?.url || "",
				);
				StoreProxy.labels.updateLabelValue(
					"STREAMLABS_CHARITY_NAME",
					this.charityTeam.title,
				);

				this.syncingCampaign = false;
				this.syncingLeaderboard = true;

				let leaderboardAmountCents = 0;

				//Get personnal raised amount from leaderboard
				const leaderBoardRes = await fetch(
					"https://streamlabscharity.com/api/v1/teams/" +
						this.charityTeam.id +
						"/leaderboards",
				);
				if (teamRes.status == 200) {
					const leaderboardJSON =
						(await leaderBoardRes.json()) as StreamlabsCharityLeaderboardData;
					const myName = StoreProxy.auth.twitch.user.displayNameOriginal.toLowerCase();
					leaderboardAmountCents = parseFloat(
						leaderboardJSON.top_streamers.find(
							(v) => v.display_name.toLowerCase() == myName,
						)?.amount || "0",
					);
					// this.charityTeam.amountRaisedPersonnal_cents = leaderboardAmountCents;
				}

				//Keep only the highest value between current and leadeboard amounts
				this.charityTeam.amountRaisedPersonnal_cents = Math.max(
					this.charityTeam.amountRaisedPersonnal_cents,
					leaderboardAmountCents,
				);
				StoreProxy.labels.updateLabelValue(
					"STREAMLABS_CHARITY_RAISED_PERSONNAL",
					this.charityTeam.amountRaisedPersonnal_cents / 100,
				);

				const newValues = [];
				if (this.charityTeam) {
					newValues.push(this.charityTeam.currency);
					newValues.push(this.charityTeam.amountRaised_cents);
					newValues.push(this.charityTeam.amountRaisedPersonnal_cents);
				}

				//Check if a value changed
				if (prevValues.join(",") != newValues.join(",")) {
					this.saveData();
					StoreProxy.donationGoals.onSourceValueUpdate(
						"streamlabs_charity",
						this.charityTeam.campaignId,
					);
				}

				StoreProxy.donationGoals.broadcastData();
				this.syncingLeaderboard = false;

				//Start full resync
				void this.resyncFromDonationList();
			} catch (error) {
				this.charityTeam = null;
				this.syncingCampaign = false;
				console.log(error);
				return false;
			}

			return true;
		},

		async resyncCharityTips(): Promise<void> {
			if (!this.charityTeam) return;
			donationPageIndex = 0;
			donationPrevPagesTotal = 0;
			this.charityTeam.amountRaisedPersonnal_cents = 0;
			StoreProxy.labels.updateLabelValue("STREAMLABS_CHARITY_RAISED_PERSONNAL", 0);
			await this.loadCharityCampaignInfo();
		},

		disconnectCharityCampaign(): void {
			this.charityTeam = null;
			this.saveData();
		},

		async resyncFromDonationList(): Promise<void> {
			if (this.syncingTips) return;
			if (!this.charityTeam) {
				this.syncingTips = false;
				return;
			}
			this.syncingTips = true;

			const me = StoreProxy.auth.twitch.user;
			let pageIndex = donationPageIndex;
			let hasResults = false;
			let total = donationPrevPagesTotal;
			let lastPageTotal = 0;
			const idsDone: Record<string, boolean> = {};
			let prevValue = this.charityTeam.amountRaisedPersonnal_cents;
			let lastTip: StreamlabsCharityDonationHistoryEntry | undefined = undefined;
			let attempts = 0;
			do {
				hasResults = false;
				try {
					const donationsRes = await fetch(
						"https://streamlabscharity.com/api/v1/teams/" +
							this.charityTeam.id +
							"/donations?page=" +
							pageIndex +
							"&ck=" +
							Date.now(),
					);
					if (donationsRes.status == 200) {
						attempts = 0;
						const donationsJSON =
							(await donationsRes.json()) as StreamlabsCharityDonationHistoryEntry[];
						hasResults = donationsJSON.length > 0;
						lastPageTotal = 0;
						if (hasResults) {
							let filtered = donationsJSON.filter((v) => {
								if (idsDone[v.id] == true) return false;
								idsDone[v.id] = true;
								return (
									v.member &&
									(v.member.user.display_name.toLowerCase() ===
										me.login.toLowerCase() ||
										v.member.user.display_name.toLowerCase() ===
											me.displayNameOriginal.toLowerCase())
								);
							});
							filtered.forEach((v) => {
								total += v.donation.converted_amount;
								lastPageTotal += v.donation.converted_amount;
							});
							if (filtered.length > 0) {
								lastTip = filtered.pop();
							}
						}
					}
					pageIndex++;
				} catch (e) {
					attempts++;
					console.error(
						"Error while fetching Streamlabs Charity donations page " + pageIndex,
						e,
					);
					if (attempts >= 50) {
						Sentry.captureMessage(
							"Failed to fetch Streamlabs Charity donations after 50 attempts",
							{
								level: "info",
								extra: {
									team: {
										id: this.charityTeam.id,
										title: this.charityTeam.title,
									},
									pageIndex,
								},
							},
						);
						break;
					}
				}
			} while (hasResults);

			//Cache all previous pages result (last one excluded) to avoid calling them all again
			donationPageIndex = pageIndex - 1;
			donationPrevPagesTotal = total - lastPageTotal;

			let currentValue =
				(StoreProxy.labels.getLabelByKey(
					"STREAMLABS_CHARITY_RAISED_PERSONNAL",
				) as number) || 0;
			this.charityTeam.amountRaisedPersonnal_cents = Math.max(currentValue * 100, total);

			DataStore.set(DataStore.STREAMLABS_CHARITY_CACHE, {
				page: donationPageIndex,
				amount: donationPrevPagesTotal,
			});
			StoreProxy.labels.updateLabelValue(
				"STREAMLABS_CHARITY_RAISED_PERSONNAL",
				this.charityTeam.amountRaisedPersonnal_cents / 100,
			);
			if (lastTip) {
				StoreProxy.labels.updateLabelValue(
					"STREAMLABS_CHARITY_LAST_TIP_AMOUNT",
					lastTip.donation.converted_amount / 100,
				);
				StoreProxy.labels.updateLabelValue(
					"STREAMLABS_CHARITY_LAST_TIP_USER",
					lastTip.member.user.display_name,
				);
			}

			if (this.charityTeam.amountRaisedPersonnal_cents != prevValue) {
				StoreProxy.donationGoals.onSourceValueUpdate(
					"streamlabs_charity",
					this.charityTeam.campaignId,
				);
				StoreProxy.donationGoals.broadcastData();
				this.saveData();
			}

			this.syncingTips = false;
		},
	} satisfies StoreActions<
		"streamlabs",
		IStreamlabsState,
		IStreamlabsGetters,
		IStreamlabsActions
	>,
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(storeStreamlabs, import.meta.hot));
}

interface StreamlabsStoreData {
	accessToken: string;
	refreshToken?: string;
	tokenExpirationDate?: number;
	socketToken: string;
	charityTeam: typeof StoreProxy.streamlabs.charityTeam;
}

interface StreamlabsWelcomeData {
	sid: string;
	upgrades: unknown[];
	pingInterval: number;
	pingTimeout: number;
	maxPayload: number;
}

export interface StreamlabsDonationData {
	type: "donation";
	message: {
		priority: number;
		isTest: boolean;
		name: string;
		amount: number | string; //Thanks SL for your consistency..
		formatted_amount: string;
		formattedAmount: string;
		message: string;
		currency: string;
		emotes: string;
		iconClassName: string;
		to: {
			name: string;
		};
		from: string;
		from_user_id: string;
		_id: string;
	}[];
	for: "streamlabs";
	event_id: string;
}

export interface StreamlabsMerchData {
	type: "merch";
	message: {
		name: string;
		isTest: boolean;
		message: string;
		to: {
			name: string;
		};
		from: string;
		product: string;
		imageHref: string;
		condition: string;
		_id: string;
		priority: 10;
	}[];
	for: "streamlabs";
	event_id: string;
}

export interface StreamlabsPatreonPledgeData {
	type: "pledge";
	message: {
		name: string;
		isTest: boolean;
		formattedAmount: string;
		/**
		 * @deprecated apparently they changed for "formattedAmount"
		 */
		formatted_amount: string;
		amount: number;
		currency: string;
		to: {
			name: string;
		};
		from: string;
		_id: string;
		priority: string;
	}[];
	for: "patreon";
	event_id: string;
}

interface StreamlabsYoutubeSponsorData {
	type: "subscription";
	message: {
		sponsorSince: string;
		id: string;
		name: string;
		channelUrl: string;
		months: number;
		_id: string;
	}[];
	for: "youtube_account";
	event_id: string;
}

interface StreamlabsYoutubeSuperchatData {
	type: "superchat";
	message: {
		id: string;
		channelId: string;
		channelUrl: string;
		name: string;
		comment: string;
		amount: string;
		currency: string;
		displayString: string;
		messageType: number;
		createdAt: string;
		_id: string;
	}[];
	for: "youtube_account";
	event_id: string;
}

interface StreamlabsCharityDonationData {
	type: "streamlabscharitydonation";
	for: "streamlabscharity";
	message: {
		charityDonationId: string;
		formattedAmount: string;
		/**
		 * @deprecated apparently they changed for "formattedAmount"
		 */
		formatted_amount: string;
		currency: string;
		amount: string;
		message: string;
		to?: {
			name: string;
		};
		memberId: string;
		from: string;
		id: number;
		userId: string;
		campaignId: string;
		createdAt: string;
		custom: unknown;
		_id: string;
		priority: number;
		senderId?: number;
		isTest?: boolean;
		isPreview?: boolean;
		unsavedSettings?: any[];
	}[];
	event_id: string;
}

interface StreamlabsCharityTeamData {
	id: string;
	display_name: string;
	slug: string;
	public: boolean;
	amount_raised: number;
	amount_raised_usd: number;
	campaign: {
		id: string;
		display_name: string;
		slug: string;
		starts_at: string;
		ends_at: string;
		currency: string;
		amount_raised: number;
		amount_raised_usd: number;
		active_milestone_widget: boolean;
		creator: {
			id: string;
			display_name: string;
			slug: string;
			is_live: boolean;
			currency: string;
			avatar: {
				url: string;
			};
		};
		causable: {
			id: string;
			display_name: string;
			slug: string;
			description: string;
			enable_fundraising: boolean;
			rank: number;
			amount_raised: number;
			has_paypal: boolean;
			has_stripe: boolean;
			external_platforms: {
				ppgf: boolean;
				benevity: boolean;
				platform_id?: any;
			};
			avatar?: {
				url: string;
			};
			page_settings: {
				header_url: string;
				website_url: string;
				video_url?: any;
				donation_box_settings: {
					opt_in: boolean;
					options: {
						option_1: {
							value: number;
							description: string;
						};
						option_2: {
							value: number;
							description: string;
						};
						option_3: {
							value: number;
							description: string;
						};
						option_4: {
							value: number;
							description: string;
						};
						description: string;
					};
					custom_donation_checkbox: boolean;
					custom_donation_checkbox_value: boolean;
					custom_donation_checkbox_display_name: string;
				};
				min_don_amount: number;
				twitter: string;
				facebook: string;
				twitch?: any;
				instagram: string;
				youtube: string;
				discord?: any;
				misc_url?: any;
				misc_url_2?: any;
				misc_url_3?: any;
				extras: {
					title_font: {
						kind: string;
						files: {
							"300": string;
							"600": string;
							"700": string;
							"800": string;
							italic: string;
							regular: string;
							"300italic": string;
							"600italic": string;
							"700italic": string;
							"800italic": string;
						};
						family: string;
						subsets: string[];
						version: string;
						category: string;
						variants: string[];
						lastModified: string;
					};
					title_weight: string;
					primary_button_color: string;
					secondary_button_color: string;
					primary_button_text_color: string;
					secondary_button_text_color: string;
				};
				toolkit_url?: any;
			};
			details: {
				country: string;
				currency: string;
			};
			tags: {
				id: string;
				name: string;
			}[];
			rating?: any;
		};
		page_settings: {
			description: string;
			header_url: string;
		};
		active_giveaway?: any;
		rewards: any[];
	};
	rewards: any[];
	members: {
		id: string;
		team_alerts: boolean;
		team_donation_goal: boolean;
		user: {
			id: string;
			display_name: string;
			slug: string;
			is_live: boolean;
			currency: string;
			avatar: {
				url: string;
			};
		};
	}[];
	goal: {
		id: string;
		amount: number;
		currency: string;
	};
}

interface StreamlabsCharityLeaderboardData {
	top_streamers: {
		amount: string;
		display_name: string;
	}[];
	top_donators: {
		display_name: string;
		amount: number;
		comment: {
			id: string;
			text: string;
		};
	}[];
}

interface StreamlabsCharityDonationHistoryEntry {
	id: string;
	donation: {
		id: string;
		display_name: string;
		amount_usd: number;
		converted_currency: string;
		converted_amount: number;
		original_currency: string;
		original_amount: number;
		team_currency: string;
		team_amount: number;
		member_currency: string;
		member_amount: number;
		country: string;
		created_at: string;
		team_member_id: string;
		comment: {
			id: string;
			text: string;
		};
		extra_information: any;
	};
	member: {
		id: string;
		user: {
			id: string;
			display_name: string;
			slug: string;
			is_live: boolean;
			currency: string;
		};
	};
}

interface StreamlabsUserData {
	streamlabs: {
		id: number;
		display_name: string;
		username: string;
		thumbnail: string;
		primary: string;
	};
	twitch: {
		id: number;
		display_name: string;
		name: string;
		icon_url: string;
	};
}
